import {
  integrationAppListResponseSchema,
  type IntegrationAppListResponse,
} from '@coworker/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider.js';
import { useCurrentWorkspace } from './use-current-workspace.js';

/**
 * The apps a person may connect - COW-491.
 *
 * **Not keyed by workspace**, unlike every other list in this app: the
 * catalogue is global (ADR-0041), so keying it per workspace would cache the
 * same nineteen rows once per workspace and refetch them on every switch. The
 * `x-workspace-id` header still goes out, because the route needs a member -
 * the key reflects what the answer depends on, not what the request carries.
 */
export const appsQueryKey = ['apps'] as const;

export interface AppsState {
  readonly query: UseQueryResult<IntegrationAppListResponse, unknown>;
}

export function useApps(): AppsState {
  const { api, status } = useAuth();
  const workspace = useCurrentWorkspace();

  const query = useQuery({
    queryKey: appsQueryKey,
    enabled: status === 'signedIn' && workspace !== undefined,
    queryFn: () =>
      api.request('/v1/apps', {
        schema: integrationAppListResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
    // A seeded catalogue changes on a deploy, not during a session.
    staleTime: 10 * 60 * 1000,
  });

  return { query };
}
